import * as Sentry from "@sentry/react";
import { queryOptions } from "@tanstack/react-query";
import { updateAnalyticsIdentity } from "../utils/analyticsIdentity";
import { enableFrontChatForClient } from "../utils/frontChatUtils";
import { currentSessionGeneration } from "../session";

export const dashboardQueryKey = ["dashboard"];

// The dashboard answers a missing, expired or invalid session with { error: <reason> }
export class DashboardAuthError extends Error {
  constructor(reason) {
    super(`Dashboard session rejected: ${reason}`);
    this.name = "DashboardAuthError";
  }
}

// Each side effect is attempted on its own, so one that throws (e.g. the
// third-party FrontChat script not having loaded yet) neither stops the others
// nor fails the load and bounces a logged-in user back to /login.
const attemptSideEffect = (fn) => {
  try {
    fn();
  } catch (err) {
    console.error("Dashboard load side-effect failed", err);
  }
};

// No AbortSignal: the response fills the shared query cache, so it's wanted
// even when the component that asked for it has unmounted.
//
// Pass { background: true } for loads made on a timer rather than because of
// anything the user did, so the backend neither counts them as activity nor
// lets them extend a client's session.
export async function fetchDashboard({ background = false } = {}) {
  const generation = currentSessionGeneration();
  const headers = background ? { "X-Juno-Background-Refresh": "true" } : {};
  const resp = await fetch("/api/dashboard", { method: "GET", credentials: "same-origin", headers });
  const data = await resp.json();
  if (data.error) {
    throw new DashboardAuthError(data.error);
  }
  if (generation !== currentSessionGeneration()) {
    return data;
  }

  // Staff viewing a client's dashboard see the same pages, so errors
  // can't otherwise be told apart from the client's own
  attemptSideEffect(() =>
    Sentry.setTags({
      session_type: data.session.session_type,
      subject_type: data.session.subject_type,
      dashboard_background_refresh: background,
    }),
  );

  // Uses session_type (who owns the session) rather than subject_type, so
  // staff viewing a dashboard are labelled "staff_app" and don't inflate
  // client or estate agent usage figures.
  attemptSideEffect(() => updateAnalyticsIdentity(data.session.email_address, data.session.session_type));

  if (data.session.session_type === "client") {
    attemptSideEffect(() => enableFrontChatForClient(data.session));
  }

  return data;
}

export const dashboardQueryOptions = queryOptions({
  queryKey: dashboardQueryKey,
  // Once there's data, only the interval starts fetches on the observer, and
  // those are made on a timer rather than by the user
  queryFn: ({ client, queryKey }) => fetchDashboard({ background: client.getQueryData(queryKey) !== undefined }),
  staleTime: 30_000,
  // Kept until resetSession, as Redux keeps it: with the 5-minute default a
  // client back from a long form would load the dashboard again
  gcTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});
